import React, { useState, useEffect } from 'react';
import { 
  TrendingDown, 
  Clock, 
  Package, 
  TrendingUp, 
  DollarSign, 
  Tag,
  Loader2,
  RefreshCw,
  ChevronRight,
  Building,
  Truck,
  Store,
  AlertTriangle,
  Sparkles,
  Filter
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const SUGGESTION_TYPES = {
  slow_moving: {
    label: 'Slow Moving',
    icon: TrendingDown,
    color: 'bg-orange-500',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    description: 'Products with low sales velocity'
  },
  near_expiry: {
    label: 'Near Expiry',
    icon: Clock,
    color: 'bg-red-500',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    description: 'Products expiring soon - promote quickly'
  },
  excess_stock: {
    label: 'Excess Stock',
    icon: Package,
    color: 'bg-purple-500',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    description: 'Overstocked items needing promotion'
  },
  fast_moving: {
    label: 'Fast Moving',
    icon: TrendingUp,
    color: 'bg-green-500',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    description: 'High demand - consider bundles/upsells'
  },
  value_deals: {
    label: 'Value Deals',
    icon: DollarSign,
    color: 'bg-blue-500',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    description: 'Great margin for promotional pricing'
  },
  supplier_offers: {
    label: 'Supplier Offers',
    icon: Tag,
    color: 'bg-teal-500',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-700',
    description: 'Special deals from suppliers'
  }
};

const SOURCE_ICONS = {
  warehouse: Truck,
  purchase: Package,
  stores: Store,
  operations: Building
};

export default function SalesProductSuggestionsWidget({ showHeader = true, compact = false }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const data = await apiRequest('/sales/product-suggestions');
      setSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      // Use mock data if API not available
      setSuggestions(getMockSuggestions());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSuggestions();
  };

  const filteredSuggestions = selectedType === 'all' 
    ? suggestions 
    : suggestions.filter(s => s.type === selectedType);

  const typeCounts = suggestions.reduce((acc, s) => {
    acc[s.type] = (acc[s.type] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full flex flex-col" data-testid="sales-suggestions-widget">
      {showHeader && (
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
              <Sparkles size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Sales Product Suggestions</h3>
              <p className="text-xs text-slate-500">{suggestions.length} items need attention</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          </Button>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="px-4 py-2 border-b border-slate-100 overflow-x-auto">
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedType('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              selectedType === 'all'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All ({suggestions.length})
          </button>
          {Object.entries(SUGGESTION_TYPES).map(([key, type]) => {
            const count = typeCounts[key] || 0;
            if (count === 0) return null;
            return (
              <button
                key={key}
                onClick={() => setSelectedType(key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  selectedType === key
                    ? `${type.color} text-white`
                    : `${type.bgColor} ${type.textColor} hover:opacity-80`
                }`}
              >
                <type.icon size={12} />
                {type.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Suggestions List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filteredSuggestions.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Package size={32} className="mx-auto mb-2 opacity-50" />
            <p>No suggestions in this category</p>
          </div>
        ) : (
          filteredSuggestions.map((suggestion, idx) => {
            const typeConfig = SUGGESTION_TYPES[suggestion.type] || SUGGESTION_TYPES.slow_moving;
            const TypeIcon = typeConfig.icon;
            const SourceIcon = SOURCE_ICONS[suggestion.source] || Building;
            
            return (
              <div
                key={idx}
                className={`p-3 rounded-xl border ${typeConfig.bgColor} border-opacity-50 hover:shadow-md transition-shadow cursor-pointer`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg ${typeConfig.color} flex items-center justify-center flex-shrink-0`}>
                    <TypeIcon size={18} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-slate-900 truncate">{suggestion.product_name}</h4>
                      {suggestion.priority === 'high' && (
                        <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{suggestion.reason}</p>
                    <div className="flex items-center gap-3 text-xs">
                      <span className={`flex items-center gap-1 ${typeConfig.textColor}`}>
                        <SourceIcon size={12} />
                        {suggestion.source_name || suggestion.source}
                      </span>
                      {suggestion.quantity && (
                        <span className="text-slate-500">
                          Qty: {suggestion.quantity}
                        </span>
                      )}
                      {suggestion.days_to_expiry && (
                        <span className="text-red-600 font-medium">
                          {suggestion.days_to_expiry} days left
                        </span>
                      )}
                      {suggestion.potential_value && (
                        <span className="text-green-600 font-medium">
                          ${suggestion.potential_value.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />
                </div>
                {suggestion.suggested_action && (
                  <div className="mt-2 pt-2 border-t border-slate-200/50">
                    <p className="text-xs text-slate-500">
                      <span className="font-medium">Suggested:</span> {suggestion.suggested_action}
                    </p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Quick Stats Footer */}
      <div className="p-3 border-t border-slate-100 bg-slate-50/50">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold text-red-600">{typeCounts.near_expiry || 0}</p>
            <p className="text-xs text-slate-500">Near Expiry</p>
          </div>
          <div>
            <p className="text-lg font-bold text-orange-600">{typeCounts.slow_moving || 0}</p>
            <p className="text-xs text-slate-500">Slow Moving</p>
          </div>
          <div>
            <p className="text-lg font-bold text-teal-600">{typeCounts.supplier_offers || 0}</p>
            <p className="text-xs text-slate-500">Supplier Deals</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Mock data for development/demo
function getMockSuggestions() {
  return [
    {
      type: 'near_expiry',
      product_name: 'Amul Butter 500g',
      reason: 'Batch expiring in 5 days - 120 units at Clyde',
      source: 'stores',
      source_name: 'Clyde Store',
      quantity: 120,
      days_to_expiry: 5,
      priority: 'high',
      potential_value: 2400,
      suggested_action: 'Run flash sale at 30% off or bundle with bread'
    },
    {
      type: 'slow_moving',
      product_name: 'Patanjali Honey 1kg',
      reason: 'Only 8 units sold in last 30 days across all stores',
      source: 'warehouse',
      source_name: 'Main Warehouse',
      quantity: 450,
      priority: 'medium',
      potential_value: 6750,
      suggested_action: 'Create combo offer with tea or breakfast items'
    },
    {
      type: 'excess_stock',
      product_name: 'Tata Salt 1kg',
      reason: 'Over-ordered by 2000 units due to bulk deal',
      source: 'purchase',
      source_name: 'Purchase Team',
      quantity: 2000,
      priority: 'low',
      potential_value: 30000,
      suggested_action: 'Push to all stores, consider B2B sales to restaurants'
    },
    {
      type: 'fast_moving',
      product_name: 'MDH Garam Masala 100g',
      reason: 'Selling 3x faster than usual - festival season',
      source: 'operations',
      source_name: 'Operations',
      quantity: 180,
      priority: 'medium',
      suggested_action: 'Create festive bundle, increase store allocation'
    },
    {
      type: 'supplier_offers',
      product_name: 'Haldirams Namkeen Range',
      reason: 'Supplier offering 25% extra margin on bulk order',
      source: 'purchase',
      source_name: 'Purchase Team',
      potential_value: 15000,
      priority: 'medium',
      suggested_action: 'Plan promotional campaign to move higher volumes'
    },
    {
      type: 'value_deals',
      product_name: 'Aashirvaad Atta 10kg',
      reason: 'Competitor pricing $2 higher - highlight value',
      source: 'operations',
      source_name: 'Operations',
      quantity: 300,
      priority: 'low',
      suggested_action: 'Feature in weekly flyer as price leader'
    },
    {
      type: 'near_expiry',
      product_name: 'Fresh Paneer 200g',
      reason: 'Batch expiring in 3 days - 80 units at Craigieburn',
      source: 'stores',
      source_name: 'Craigieburn Store',
      quantity: 80,
      days_to_expiry: 3,
      priority: 'high',
      potential_value: 800,
      suggested_action: 'Immediate 50% markdown, promote paneer recipes'
    },
    {
      type: 'slow_moving',
      product_name: 'Organic Quinoa 500g',
      reason: 'Premium product not moving in budget-focused stores',
      source: 'warehouse',
      source_name: 'Main Warehouse',
      quantity: 200,
      priority: 'medium',
      potential_value: 3000,
      suggested_action: 'Relocate to Hoppers Crossing, target health-conscious segment'
    }
  ];
}
